import { zodResolver } from '@hookform/resolvers/zod';
import { ElementRef, useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { ConfirmModal } from './ConfirmModal';
import { FormCheckbox } from './FormCheckbox';
import { IntegrationSwitch } from './IntegrationSwitch';

import { useGetSeo } from '@/hooks/useGetSeo';
import { useUpdateSeo } from '@/hooks/useUpdateSeo';

const integrationFormSchema = z.object({
  productDescription: z.boolean(),
  altDescriptionProduct: z.boolean(),
  productTags: z.boolean(),
  altDescriptionImage: z.boolean(),
});

export type IntegrationFormData = z.infer<typeof integrationFormSchema>;

export function IntegrationForm() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.SeoContent.components.IntegrationForm',
  });
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const submitButtonRef = useRef<ElementRef<'button'>>(null);

  const { seo } = useGetSeo();

  const { updateSeo } = useUpdateSeo();

  const { control, handleSubmit } = useForm<IntegrationFormData>({
    resolver: zodResolver(integrationFormSchema),
    defaultValues: {
      productDescription: !!seo?.productDescription,
      altDescriptionProduct: !!seo?.altDescriptionProduct,
      productTags: !!seo?.productTags,
      altDescriptionImage: !!seo?.altDescriptionImage,
    },
  });

  const handleToggleConfirmModal = useCallback(() => {
    setIsConfirmModalOpen((state) => !state);
  }, []);

  const handleClickSubmitButton = useCallback(() => {
    submitButtonRef.current?.click();
    setIsConfirmModalOpen(!isConfirmModalOpen);
  }, [isConfirmModalOpen]);

  const handleIntegration = handleSubmit(async (data) => {
    await updateSeo({
      ...data,
      productName: false,
      sync: !seo?.sync,
    });
  });

  if (!seo) {
    return null;
  }

  return (
    <>
      <form className="space-y-4" onSubmit={handleIntegration} noValidate>
        <IntegrationSwitch
          isActive={!!seo?.sync}
          onToggleConfirmModal={handleToggleConfirmModal}
        />

        <div className="mt-4 grid grid-cols-2 gap-4">
          <FormCheckbox
            name="productDescription"
            label={t('labels.productDescription')}
            disabled={seo.sync}
            control={control}
          />

          <FormCheckbox
            name="altDescriptionProduct"
            label={t('labels.altDescriptionProduct')}
            disabled={seo.sync}
            control={control}
          />

          <FormCheckbox
            name="productTags"
            label={t('labels.productTags')}
            disabled={seo.sync}
            control={control}
          />

          <FormCheckbox
            name="altDescriptionImage"
            label={t('labels.altDescriptionImage')}
            disabled={seo.sync}
            control={control}
          />
        </div>

        <button ref={submitButtonRef} type="submit" className="hidden" />
      </form>

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        isSyncEnabled={seo.sync}
        onToggle={handleToggleConfirmModal}
        onConfirm={handleClickSubmitButton}
      />
    </>
  );
}
